import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { Logo } from "./Logo";
import { PHONE, PHONE_HREF } from "../lib/site";
import { SERVICES } from "../lib/services";

const ServicesMenu = () => (
  <div className="group relative">
    <Link to="/services" data-testid="nav-services-trigger" className="flex items-center gap-1 text-sm text-slate-300 transition-colors hover:text-white">
      Services <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180" />
    </Link>
    <div className="invisible absolute left-1/2 top-full w-[380px] -translate-x-1/2 pt-4 opacity-0 transition-[opacity,visibility] duration-300 group-hover:visible group-hover:opacity-100">
      <div className="rounded-2xl border border-white/10 bg-ink/95 p-2 backdrop-blur-xl">
        <Link to="/services" data-testid="nav-service-all" className="mb-1 flex items-center justify-between rounded-xl border-b border-white/10 px-4 py-3 text-sm font-semibold text-white hover:bg-white/5">
          All services <ArrowUpRight className="h-4 w-4 text-glint" />
        </Link>
        {SERVICES.map((s) => (
          <Link key={s.slug} to={s.path} data-testid={`nav-service-${s.slug}`} className="flex items-center justify-between rounded-xl px-4 py-3 text-sm text-slate-200 transition-colors hover:bg-white/5 hover:text-white">
            <span><span className="mr-3 font-mono text-xs text-glint">{s.no}</span>{s.nav}</span>
            <ArrowUpRight className="h-4 w-4 opacity-50" />
          </Link>
        ))}
      </div>
    </div>
  </div>
);

const MobileMenu = ({ onClose }) => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="fixed inset-0 z-[60] flex flex-col bg-ink px-6 pb-10 pt-24 text-white lg:hidden"
    data-testid="mobile-menu"
  >
    <button onClick={onClose} data-testid="mobile-menu-close" aria-label="Close menu" className="absolute right-5 top-5 rounded-full border border-white/15 p-3">
      <X className="h-5 w-5" />
    </button>
    <nav className="flex flex-col gap-1">
      {[{ path: "/", nav: "Home" }, { path: "/services", nav: "All Services" }, ...SERVICES, { path: "/about", nav: "About" }, { path: "/contact", nav: "Contact" }].map((l, i) => (
        <motion.div key={l.path} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }}>
          <Link to={l.path} onClick={onClose} data-testid={`mobile-nav-${l.path.replace(/\W+/g, "-") || "home"}`} className="block border-b border-white/10 py-3 font-display text-xl">
            {l.nav}
          </Link>
        </motion.div>
      ))}
    </nav>
    <div className="mt-auto grid gap-3">
      <a href={PHONE_HREF} data-testid="mobile-menu-call" className="flex items-center justify-center gap-2 rounded-full border border-white/20 py-4"><Phone className="h-4 w-4" /> {PHONE}</a>
      <Link to="/request-quote" onClick={onClose} data-testid="mobile-menu-quote" className="rounded-full bg-glint py-4 text-center font-semibold text-ink">Request a Free Cleaning Quote</Link>
    </div>
  </motion.div>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.8, delay: 0.1 }}
        className={`fixed inset-x-0 top-0 z-50 border-b text-white backdrop-blur-xl transition-colors duration-500 ${scrolled ? "border-white/10 bg-ink/85" : "border-transparent bg-ink/60"}`}
        data-testid="site-header"
      >
        <div className="mx-auto flex h-[72px] max-w-[1400px] items-center justify-between px-5 md:px-10">
          <Link to="/" data-testid="header-logo-link" aria-label="Shine Solutions home"><Logo /></Link>
          <nav className="hidden items-center gap-9 lg:flex">
            <ServicesMenu />
            <NavLink to="/about" data-testid="nav-about" className="text-sm text-slate-300 transition-colors hover:text-white">About</NavLink>
            <NavLink to="/contact" data-testid="nav-contact" className="text-sm text-slate-300 transition-colors hover:text-white">Contact</NavLink>
          </nav>
          <div className="flex items-center gap-3">
            <a href={PHONE_HREF} data-testid="header-call-btn" className="hidden items-center gap-2 text-sm text-slate-200 transition-colors hover:text-glint md:flex">
              <Phone className="h-4 w-4" /> {PHONE}
            </a>
            <span className="hidden sm:block"><Link to="/request-quote" data-testid="nav-quote-btn" className="btn-glint">Request a Free Quote</Link></span>
            <button onClick={() => setOpen(true)} data-testid="mobile-menu-open" aria-label="Open menu" className="rounded-full border border-white/15 p-2.5 lg:hidden">
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </motion.header>
      <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
};
